import React, {createContext, useContext, useMemo} from "react";
import { useStorageReducer } from 'react-storage-hooks';
import {AppContext} from "./AppContext";
import uuid from "../components/helpers/uuid";

const reducer = (state, action) => {
    if (process.env.NODE_ENV === 'development') {
        console.log('session reducer:', action.type, action);
    }

    switch(action.type) {
        case 'set_org':
            clearSession({preserveUploadBatches: true})
            return {...state, currentOrg: action.org, abilities: action.abilities };

        case 'org_updated':
            return {...state, currentOrg: action.org };

        case 'setCanUpload':
            return {...state, canUpload: action.canUpload };

        case 'setTaxonomyFilterMode':
            const taxonomyFilterModes = {...(state.taxonomyFilterModes || {})};
            taxonomyFilterModes[action.id] = action.mode;
            return {...state, taxonomyFilterModes}

        case 'setViewMode':
            return {...state, viewMode: action.viewMode };

        case 'setGridSize':
            return {...state, gridSize: action.gridSize };

        case 'setGridColor':
            return {...state, gridColor: action.gridColor };

        case 'setShowFilenames':
            return {...state, showFilenames: action.showFilenames };

        case 'setAutoPlay':
            return {...state, autoPlay: action.autoPlay };

        case 'setShowIcons':
            return {...state, showIcons: action.showIcons };

        case 'setShowRating':
            return {...state, showRating: action.showRating };

        case 'setShowHeadline':
            return {...state, showHeadline: action.showHeadline };

        case 'setColorLabel':
            return {...state, colorLabel: action.colorLabel };

        case 'setTocHidden':
            return {...state, tocHidden: action.tocHidden };

        case 'setConvertSpacesToOr':
            return {...state, convertSpacesToOr: action.convertSpacesToOr };

        case 'setConvertCommasToOr':
            return {...state, convertCommasToOr: action.convertCommasToOr };

        case 'logout':
            clearSession()
            return {...state, currentOrg: null, abilities: [], canUpload: false };

        default:
            return state;
    }
};

const initialState = {
    taxonomyFilterModes: {},
    editingAssetGroup: null,
    tocHidden: false,
    sessionGuid: uuid(),
    canUpload: false,
    currentOrg: null,
    abilities: [],
};

const SessionContext = createContext(initialState);

function SessionProvider(props) {
    const key = `session-reducer`;
    const [state, dispatch, writeError] = useStorageReducer(sessionStorage, key, reducer, initialState);

    window.clearSession = ({preserveUploadBatches = false} = {})=> {
        // Organization rehydration also runs on full-page reload. Preserve only
        // the explicitly user/org-scoped current batches, not other session data.
        // Logout uses the default and still removes every saved batch.
        const uploadBatches = preserveUploadBatches ? Object.keys(sessionStorage)
            .filter(key => /^current-upload-batches:\d+:\d+$/.test(key))
            .map(key => [key, sessionStorage.getItem(key)]) : [];
        sessionStorage.clear()
        uploadBatches.forEach(([key, value]) => sessionStorage.setItem(key, value));
        
        // Clear all cookies
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i];
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        }
    }

    const contextValue = useMemo(() => ({state, dispatch}), [state, dispatch])

    // TODO: return error display if writeError
    return <SessionContext.Provider value={contextValue} {...props}/>;
}

export { SessionContext, SessionProvider };
